<template>
  <div class="databank">
    <div class="vp-page-head">
      <div class="vp-min-w-0">
        <h1 class="vp-page-title">{{ $t('databank.title') }}</h1>
        <div class="vp-page-sub">
          <i class="ph-duotone ph-database"></i>
          <span>{{ $t('databank.description') }}</span>
        </div>
      </div>
    </div>

    <div v-if="error" class="vp-note vp-note--danger vp-stack-bottom">
      <i class="ph-duotone ph-warning-circle"></i>
      <div class="vp-min-w-0">{{ error }}</div>
      <button type="button" class="vp-note-dismiss" :aria-label="$t('common.close')" @click="error = null">
        <i class="ph-duotone ph-x"></i>
      </button>
    </div>

    <div v-if="success" class="vp-note vp-note--success vp-stack-bottom">
      <i class="ph-duotone ph-check-circle"></i>
      <div class="vp-min-w-0">{{ success }}</div>
      <button type="button" class="vp-note-dismiss" :aria-label="$t('common.close')" @click="success = null">
        <i class="ph-duotone ph-x"></i>
      </button>
    </div>

    <div class="databank-grid">
      <router-link v-for="section in sections" :key="section.to" :to="section.to" class="databank-card vp-card">
        <span class="databank-icon" :class="section.chip"><i :class="section.icon"></i></span>
        <div class="databank-body">
          <h2 class="databank-title">{{ section.title }}</h2>
          <p class="databank-text">{{ section.text }}</p>
        </div>
        <span class="databank-cta">
          {{ section.cta }}<i class="ph-duotone ph-arrow-right"></i>
        </span>
      </router-link>

      <div class="databank-card databank-card--placeholder">
        <span class="databank-icon vp-chip--muted"><i class="ph-duotone ph-plus-circle"></i></span>
        <div class="databank-body">
          <h2 class="databank-title">{{ $t('databank.comingSoon.title') }}</h2>
          <p class="databank-text">{{ $t('databank.comingSoon.description') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const error = ref(null);
const success = ref(null);

const currentUser = computed(() => store.getters['auth/currentUser']);

const sections = computed(() => [
  {
    to: '/databank/certificates',
    icon: 'ph-duotone ph-certificate',
    chip: 'vp-chip--primary',
    title: t('databank.certificates.title'),
    text: t('databank.certificates.description'),
    cta: t('databank.certificates.manage')
  },
  {
    to: '/databank/company-types',
    icon: 'ph-duotone ph-buildings',
    chip: 'vp-chip--primary',
    title: t('databank.companyTypes.title'),
    text: t('databank.companyTypes.description'),
    cta: t('databank.companyTypes.manage')
  },
  {
    to: '/databank/mail-template',
    icon: 'ph-duotone ph-envelope-simple',
    chip: 'vp-chip--info',
    title: t('databank.mailTemplate.title'),
    text: t('databank.mailTemplate.description'),
    cta: t('databank.mailTemplate.manage')
  },
  {
    to: '/databank/category',
    icon: 'ph-duotone ph-list-tree',
    chip: 'vp-chip--success',
    title: t('databank.category.title'),
    text: t('databank.category.description'),
    cta: t('databank.category.manage')
  },
  {
    to: '/pdf-templates',
    icon: 'ph-duotone ph-file-pdf',
    chip: 'vp-chip--danger',
    title: t('databank.pdfTemplate.title'),
    text: t('databank.pdfTemplate.description'),
    cta: t('databank.pdfTemplate.manage')
  },
  {
    to: '/databank/user-guide',
    icon: 'ph-duotone ph-book-open',
    chip: 'vp-chip--warning',
    title: t('databank.userGuide.title'),
    text: t('databank.userGuide.description'),
    cta: t('databank.userGuide.manage')
  }
]);

onMounted(() => {
  if (!currentUser.value?.isSuperAdmin) {
    store.dispatch('notify', {
      message: t('common.notification.unauthorized'),
      type: 'error'
    });
    router.push('/admin');
  }
});
</script>

<style scoped>
.databank-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 14px;
}

.databank-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px;
  color: inherit;
  text-decoration: none;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}

a.databank-card:hover {
  box-shadow: var(--vp-shadow-card-hover);
  transform: translateY(-2px);
  text-decoration: none;
}

.databank-card--placeholder {
  border: 1px dashed var(--vp-gray-300);
  background: var(--vp-gray-100);
  box-shadow: none;
}

.databank-icon {
  width: 42px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--vp-radius);
  font-size: 1.25rem;
}

.databank-body {
  flex: 1;
}

.databank-title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--vp-heading-color);
}

.databank-text {
  margin: 4px 0 0;
  font-size: 0.8125rem;
  color: var(--vp-muted-color);
}

.databank-cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--vp-primary);
}
</style>
